import { useRef, useEffect } from 'react';

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';

import TripTableRow from './TripTableRow';

import Groups3Icon from '@mui/icons-material/Groups3';
import TimelineIcon from '@mui/icons-material/Timeline';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ShapeLineIcon from '@mui/icons-material/ShapeLine';
import ArrowCircleRightIcon from '@mui/icons-material/ArrowCircleRight';
import ArrowCircleLeftIcon from '@mui/icons-material/ArrowCircleLeft';
import AlarmOnIcon from '@mui/icons-material/AlarmOn';
import AlarmOffIcon from '@mui/icons-material/AlarmOff';
import DirectionsBusIcon from '@mui/icons-material/DirectionsBus';
import { tripTableWidths } from '../utils/table/tripTable';
import { useCurrentHash } from '../hooks/hashs/useCurrentHash';
import { useLayoutStore } from '../hooks/screen/useLayoutStore';
import { useScreenStore } from '../stores/useScreenStore';
import { useOperationalMapStore } from '../stores/useOperationalMapStore';

const TripTableView = ({trips, endOfListRef, resetScrollKey}) => {

  const containerRef = useRef(null);

  const { hashColor } = useCurrentHash();

  const isCompactMode = useLayoutStore(
    state => state.isCompactMode
  );

  const monitorMode = useScreenStore(state => state.monitorMode);

  const isOperationalMapMode = useOperationalMapStore(state => state.isOperationalMapMode);

  useEffect(() => {

    containerRef.current?.scrollTo({
      top: 0,
      behavior: 'auto'
    });

  }, [resetScrollKey]);

  return (
  
    <TableContainer
      data-compact-scroll-ignore
      ref={containerRef}
      sx={{
        flex: 1,
        overflow: 'auto',
        pl: (monitorMode === 1 && (isCompactMode && !isOperationalMapMode)) ? 8 : 0
      }}
    >

      <Table stickyHeader sx={{tableLayout: 'fixed'}}>

        <TableHead
          sx={(theme) => ({
            '& .MuiTableCell-root': {
              backgroundColor:
                theme.palette[hashColor]?.main,
              color: theme.palette.getContrastText(
                theme.palette[hashColor]?.main
              ),
              fontWeight: 600,
              borderTop: 1,
              borderTopColor: theme.palette.getContrastText(
                theme.palette[hashColor]?.main
              ),
              whiteSpace: 'pre-line',
              lineHeight: 1
            }
          })}
        >
          <TableRow>

            <TableCell align="center" sx={{width: tripTableWidths.linha}}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4
                }}
              >
                <TimelineIcon sx={{ fontSize: 20 }} />
                <span>LINHA</span>
              </div>
            </TableCell>

            <TableCell align="center" sx={{width: tripTableWidths.sentido}}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4
                }}
              >
                <ArrowCircleLeftIcon sx={{ fontSize: 20 }}/>
                <span>SENTIDO</span>
                <ArrowCircleRightIcon sx={{ fontSize: 20 }}/>
              </div>
            </TableCell>

            <TableCell align="center" sx={{width: tripTableWidths.cliente}}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4
                }}
              >
                <Groups3Icon sx={{ fontSize: 20 }} />
                <span>CLIENTE</span>
              </div>
            </TableCell>

            <TableCell align="center" sx={{width: tripTableWidths.veiculo}}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4
                }}
              >
                <DirectionsBusIcon sx={{ fontSize: 20 }} />
                <span>VEÍCULO</span>
              </div>
            </TableCell>

            <TableCell align="center" sx={{width: tripTableWidths.unidade}}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4
                }}
              >
                <WarehouseIcon sx={{ fontSize: 20 }} />
                <span>UNIDADE</span>
              </div>
            </TableCell>
                
            <TableCell align="center" sx={{width: tripTableWidths.codigo}}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4
                }}
              >
                <ShapeLineIcon sx={{ fontSize: 20 }} />
                <span>CÓDIGO</span>
              </div>
            </TableCell>

            <TableCell align="center" sx={{width: tripTableWidths.pontosatendidos}}>
              P/A
            </TableCell>

            <TableCell align="center" sx={{width: tripTableWidths.progresso}}>
              P
            </TableCell>

            <TableCell align="center" sx={{width: tripTableWidths.inicioPrevisto}}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4
                }}
              >
                <AlarmOnIcon sx={(theme) => ({ fontSize: 20, color: theme.palette['white']?.main })}/>
                <Typography sx={(theme) => ({ marginLeft: -1.5, fontSize: 20, fontWeight: 'bold', color: theme.palette['white']?.main })}>P</Typography>
                {'\n'}
                INÍCIO
              </div>
            </TableCell>

            <TableCell align="center" sx={{width: tripTableWidths.inicioReal}}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4
                }}
              >
                <AlarmOnIcon sx={(theme) => ({ fontSize: 20, color: theme.palette['white']?.main })}/>
                <Typography sx={(theme) => ({ marginLeft: -1.5, fontSize: 20, fontWeight: 'bold', color: theme.palette['white']?.main })}>R</Typography>
                {'\n'}
                INÍCIO
              </div>
            </TableCell>

            <TableCell align="center" sx={{width: tripTableWidths.finalPrevisto}}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4
                }}
              >
                <AlarmOffIcon sx={(theme) => ({ fontSize: 20, color: theme.palette['white']?.main })}/>
                <Typography sx={(theme) => ({ marginLeft: -1.5, fontSize: 20, fontWeight: 'bold', color: theme.palette['white']?.main })}>P</Typography>
                {'\n'}
                FINAL
              </div>
            </TableCell>

            <TableCell align="center" sx={{width: tripTableWidths.finalReal}}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 4
                }}
              >
                <AlarmOnIcon sx={(theme) => ({ fontSize: 20, color: theme.palette['white']?.main })}/>
                <Typography sx={(theme) => ({ marginLeft: -1.5, fontSize: 20, fontWeight: 'bold', color: theme.palette['white']?.main })}>R</Typography>
                {'\n'}
                FINAL
              </div>
            </TableCell>

            <TableCell align="center" sx={{width: tripTableWidths.status}}>
              STATUS
            </TableCell>

          </TableRow>
        </TableHead>

        <TableBody>

          {trips.map(trip => (
            <TripTableRow
              key={`${trip.CODIGO_INTEGRACAO}-${trip.VIAGEM_ID || ''}`}
              trip={trip}
            />
          ))}

          <TableRow>
            <TableCell colSpan={999}>
              <div
                ref={endOfListRef}
                style={{ height: 10 }}
              />
            </TableCell>
          </TableRow>

        </TableBody>

      </Table>

    </TableContainer>
  );
};

export default TripTableView;